import React, { useEffect, useState, useContext } from 'react';
import apiClient from '../../apiClient';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../UserContext';
import { isClinicLevelAdmin } from '../../utils/permissions';
import { toast } from 'react-toastify';
import './JournalTemplateList.css';
import TemplateGenerator from '../TemplateGenerator/TemplateGenerator';

function JournalTemplateList() {
  const [templates, setTemplates] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [showDropdownId, setShowDropdownId] = useState(null);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvMetadata, setCsvMetadata] = useState({
    templateName: '',
    clinicId: '',
    templatePrompt: ''
  });
  const [showGenerator, setShowGenerator] = useState(false);

  /* ------- Hämta clinics + mallar ---------- */
  useEffect(() => {
    apiClient.get('/api/clinics')
      .then(res => setClinics(res.data))
      .catch(err => console.error('Fel vid hämtning av clinics:', err));

    apiClient.get('/api/journalmallar')
      .then(res => setTemplates(res.data))
      .catch(err => {
        console.error('Fel vid hämtning av mallar:', err);
        setError('Misslyckades att hämta mallar.');
      });
  }, []);

  /* ------- CRUD‑handlers ---------- */
  const handleEdit = (id)   => navigate(`/journalmallar/edit/${id}`);
  const handleCreate = ()   => navigate('/journalmallar/create');
  const handleDelete = (id) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna journalmall?')) return;

    apiClient.delete(`/api/journalmallar/${id}`)
      .then(() => setTemplates(templates.filter(t => t.template_id !== id)))
      .catch(err => {
        console.error('Fel vid borttagning av mall:', err);
        setError('Misslyckades att ta bort mall.');
      });
  };

  const copyTemplate = async (templateId, clinicId) => {
    try {
      await apiClient.post(`/api/journalmallar/${templateId}/copy`, {
        clinic_id: clinicId
      });
      toast.success('Mall kopierad!');
      // Refresh templates list after successful copy
      apiClient.get('/api/journalmallar')
        .then(res => setTemplates(res.data))
        .catch(err => console.error('Fel vid uppdatering av mallar:', err));
    } catch (err) {
      console.error('Kopiering misslyckades:', err);
      toast.error(err.response?.data?.msg || 'Kopiering misslyckades');
    }
  };

  /* ------- CSV Export Handler ---------- */
  const exportToCSV = (template) => {
    if (!template.sections || template.sections.length === 0) {
      toast.error('Mallen har inga sektioner att exportera');
      return;
    }

    // Build CSV content with semicolon separator (matching import format)
    const csvLines = template.sections.map(section => {
      // Escape quotes by doubling them, and wrap in quotes if content contains special chars
      const escapeField = (field) => {
        const str = field || '';
        if (str.includes(';') || str.includes(',') || str.includes('"') || str.includes('\n')) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };

      return `${escapeField(section.section_title)};${escapeField(section.content)}`;
    });

    const csvContent = csvLines.join('\n');

    // Create blob with BOM for Excel compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${template.template_name.replace(/[^a-zA-Z0-9åäöÅÄÖ\s-]/g, '_')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Mall "${template.template_name}" exporterad!`);
  };

  /* ------- CSV Upload Handlers ---------- */
  const handleCsvFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/csv' || file.type === 'application/vnd.ms-excel' || file.name.toLowerCase().endsWith('.csv'))) {
      setCsvFile(file);
      setShowCsvModal(true);
    } else {
      toast.error('Vänligen välj en giltig CSV-fil');
    }
  };

  const parseCsvContent = (csvText) => {
    // Remove BOM if present
    let cleanText = csvText.replace(/^\uFEFF/, '');

    // Normalize line endings
    cleanText = cleanText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    const sections = [];

    // Detect separator - count occurrences outside of quoted strings
    let semicolonCount = 0;
    let commaCount = 0;
    let inQuote = false;
    for (let i = 0; i < cleanText.length; i++) {
      const char = cleanText[i];
      if (char === '"') {
        // Check for escaped quote
        if (inQuote && cleanText[i + 1] === '"') {
          i++; // Skip next quote
        } else {
          inQuote = !inQuote;
        }
      } else if (!inQuote) {
        if (char === ';') semicolonCount++;
        if (char === ',') commaCount++;
      }
    }
    const separator = semicolonCount >= commaCount ? ';' : ',';

    console.log('CSV parsing:', { semicolonCount, commaCount, separator });

    // Parse entire CSV content character by character
    const records = [];
    let currentField = '';
    let currentRecord = [];
    let inQuotes = false;
    let i = 0;

    while (i < cleanText.length) {
      const char = cleanText[i];
      const nextChar = cleanText[i + 1];

      if (char === '"') {
        if (!inQuotes) {
          // Starting a quoted field
          inQuotes = true;
        } else if (nextChar === '"') {
          // Escaped quote inside quoted field
          currentField += '"';
          i++; // Skip the next quote
        } else {
          // Ending a quoted field
          inQuotes = false;
        }
      } else if (char === separator && !inQuotes) {
        // Field separator
        currentRecord.push(currentField);
        currentField = '';
      } else if (char === '\n' && !inQuotes) {
        // End of record
        currentRecord.push(currentField);
        if (currentRecord.some(f => f.trim())) {
          records.push(currentRecord);
        }
        currentRecord = [];
        currentField = '';
      } else {
        currentField += char;
      }
      i++;
    }

    // Don't forget the last field/record
    if (currentField || currentRecord.length > 0) {
      currentRecord.push(currentField);
      if (currentRecord.some(f => f.trim())) {
        records.push(currentRecord);
      }
    }

    // Process each record
    for (const record of records) {
      if (record.length >= 2) {
        const title = record[0].trim();
        const content = record[1].trim();

        // Only require title to be non-empty (content can be empty)
        if (title) {
          console.log('Parsed section:', { title, content: content.substring(0, 100) + '...' });
          sections.push({
            section_title: title,
            content: content || '', // Allow empty content
            tabs_needed: 0
          });
        }
      } else if (record.length === 1 && record[0].trim()) {
        // Single column - treat as title with empty content
        console.log('Parsed section (single column):', { title: record[0].trim() });
        sections.push({
          section_title: record[0].trim(),
          content: '',
          tabs_needed: 0
        });
      }
    }

    console.log('Final sections count:', sections.length);
    return sections;
  };

  const handleCsvUpload = async () => {
    if (!csvFile || !csvMetadata.templateName || !csvMetadata.clinicId) {
      toast.error('Vänligen fyll i alla obligatoriska fält');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const csvText = e.target.result;
        const sections = parseCsvContent(csvText);
        
        if (sections.length === 0) {
          toast.error('Inga giltiga sektioner hittades i CSV-filen');
          return;
        }

        const payload = {
          template_name: csvMetadata.templateName,
          template_prompt: csvMetadata.templatePrompt,
          clinic_id: csvMetadata.clinicId,
          is_pmo_template: false,
          template_type: 'journal',
          sections: sections.map((s, idx) => ({
            section_title: s.section_title,
            content: s.content,
            order: idx,
            tabs_needed: s.tabs_needed || 0
          }))
        };

        try {
          await apiClient.post('/api/journalmallar', payload);
          toast.success('Mall skapad från CSV!');
          setShowCsvModal(false);
          setCsvFile(null);
          setCsvMetadata({ templateName: '', clinicId: '', templatePrompt: '' });
          
          // Refresh templates list
          apiClient.get('/api/journalmallar')
            .then(res => setTemplates(res.data))
            .catch(err => console.error('Fel vid uppdatering av mallar:', err));
        } catch (err) {
          console.error('Fel vid skapande av mall:', err);
          toast.error('Misslyckades att skapa mall från CSV');
        }
      };
      
      reader.readAsText(csvFile, 'UTF-8');
    } catch (err) {
      console.error('Fel vid läsning av CSV:', err);
      toast.error('Misslyckades att läsa CSV-fil');
    }
  };

  /* ------- Hjälpstrukturer ---------- */
  const clinicMap = clinics.reduce((acc, c) => ({ ...acc, [c.clinic_id]: c.clinic_name }), {});
  const filteredTemplates = selectedClinicId
    ? templates.filter(t => t.clinic_id === parseInt(selectedClinicId, 10))
    : templates;

  /* --------------- JSX ---------------- */
  return (
    <div style={{ padding: '20px' }}>
      <h2>Journalmallar</h2>

      {/* Filtrering */}
      <div style={{ marginBottom: '10px' }}>
        <label htmlFor="clinic-filter">Filtrera efter klinik: </label>
        <select
          id="clinic-filter"
          value={selectedClinicId}
          onChange={(e) => setSelectedClinicId(e.target.value)}
        >
          <option value="">-- Alla kliniker --</option>
          {clinics.map(c => (
            <option key={c.clinic_id} value={c.clinic_id}>{c.clinic_name}</option>
          ))}
        </select>
      </div>

      {isClinicLevelAdmin(user) && (
        <div style={{ marginBottom: '20px' }}>
          <button onClick={handleCreate} className="create-button">Skapa Ny Mall</button>
          <button onClick={() => setShowGenerator(true)} className="generate-button">
            Generera Mall med AI
          </button>
          <input
            type="file"
            accept=".csv"
            onChange={handleCsvFileSelect}
            style={{ display: 'none' }}
            id="csv-upload"
          />
          <label htmlFor="csv-upload" className="csv-upload-button">
            Ladda upp CSV-mall
          </label>
        </div>
      )}
      {error && <p style={{ color: 'red' }}>{error}</p>}

      <table className="journal-template-table">
        <thead>
          <tr>
            <th>Mallnamn</th>
            <th>Klinik</th>
            <th>Åtgärder</th>
          </tr>
        </thead>
        <tbody>
          {filteredTemplates.map(t => (
            <tr key={t.template_id}>
              <td>{t.template_name}</td>
              <td>{clinicMap[t.clinic_id] || 'Okänd klinik'}</td>
              <td>
                <div className="action-buttons">
                  {user?.is_admin && clinics.length > 0 && (
                    <div className="copy-dropdown-container">
                      <button
                        className="copy-button"
                        onClick={() => setShowDropdownId(showDropdownId === t.template_id ? null : t.template_id)}
                      >
                        Kopiera till…
                      </button>
                      {showDropdownId === t.template_id && (
                        <div className="clinic-dropdown-menu">
                          {clinics.map(c => (
                            <div
                              key={c.clinic_id}
                              className="clinic-dropdown-item"
                              onClick={() => {
                                copyTemplate(t.template_id, c.clinic_id);
                                setShowDropdownId(null);
                              }}
                            >
                              {c.clinic_name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {isClinicLevelAdmin(user) && (
                    <>
                      <button onClick={() => exportToCSV(t)} className="export-button">Exportera</button>
                      <button onClick={() => handleEdit(t.template_id)} className="edit-button">Redigera</button>
                      <button onClick={() => handleDelete(t.template_id)} className="delete-button">Ta Bort</button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* CSV Upload Modal */}
      {showCsvModal && (
        <div className="modal-overlay" onClick={() => setShowCsvModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Ladda upp CSV-mall</h3>
            <p><strong>Format:</strong> CSV-filen ska ha två kolumner: Rubriknamn, Instruktion</p>
            <p><strong>Exempel (komma eller semikolon fungerar):</strong></p>
            <pre style={{ background: '#f5f5f5', padding: '10px', fontSize: '12px' }}>
              Anamneses;Skrive en anamneses
              aktuellt;Skriv vad som hänt
              status;Vad är status
              åtgärd;Vilken åtgärd har genomförts
              planering;Vad är planeringen?
            </pre>

            <div className="form-group">
              <label>Mallnamn *:</label>
              <input
                type="text"
                value={csvMetadata.templateName}
                onChange={(e) => setCsvMetadata({...csvMetadata, templateName: e.target.value})}
                placeholder="Ange mallnamn..."
                required
              />
            </div>

            <div className="form-group">
              <label>Klinik *:</label>
              <select
                value={csvMetadata.clinicId}
                onChange={(e) => setCsvMetadata({...csvMetadata, clinicId: e.target.value})}
                required
              >
                <option value="">-- Välj Klinik --</option>
                {clinics.map(c => (
                  <option key={c.clinic_id} value={c.clinic_id}>{c.clinic_name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Mallspecifik prompt (valfritt):</label>
              <textarea
                rows="3"
                value={csvMetadata.templatePrompt}
                onChange={(e) => setCsvMetadata({...csvMetadata, templatePrompt: e.target.value})}
                placeholder="Skriv eventuell extra prompt för denna journalmall..."
              />
            </div>

            <div className="modal-actions">
              <button onClick={handleCsvUpload} className="save-button">
                Skapa Mall
              </button>
              <button onClick={() => {
                setShowCsvModal(false);
                setCsvFile(null);
                setCsvMetadata({ templateName: '', clinicId: '', templatePrompt: '' });
              }} className="cancel-button">
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Generator Modal */}
      <TemplateGenerator
        isOpen={showGenerator}
        onClose={() => setShowGenerator(false)}
        clinics={clinics}
        onTemplateCreated={() => {
          // Refresh templates list
          apiClient.get('/api/journalmallar')
            .then(res => setTemplates(res.data))
            .catch(err => console.error('Fel vid uppdatering av mallar:', err));
        }}
      />
    </div>
  );
}

export default JournalTemplateList;
